import { formatDate } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, H2, Icon, Separator, Shimmer, Table, Text } from '@betterment/samba-react';

import { useCurrentEmployerId } from '~/b4b/utils/currentEmployer';
import { BenefitReportStatus } from '~/graphql/schema';

import { GeneratedReportsDocument, GeneratedReportsQuery } from './graphql/query.generatedReports.generated';

import { ErrorBoundary } from '../../../components/error-boundary/ErrorBoundary';
import { SambaAutoLink } from '../../../components/SambaAutoLink';
import { urlBuilder } from '../../../utils/urlBuilder';
import { isReportComplete } from '../reports-notifications/Notifications';

type Report = {
  id: string;
  createdAt: Date;
  name: string;
  contentType: string;
  status: BenefitReportStatus;
  filename: string;
};

type DeconversionReport = Report & {
  liquidationDate?: Date | null;
};

type GeneralReport = Report & {
  startDate?: Date | null;
  endDate?: Date | null;
};

const formatDateRange = (startDate: string | Date | null | undefined, endDate: string | Date | null | undefined) => {
  if (!startDate) return formatDate(endDate!, 'longOrdinalSlashed');
  if (!endDate) return formatDate(startDate, 'longOrdinalSlashed');

  return `${formatDate(startDate, 'longOrdinalSlashed')}${String.fromCharCode(8211)}${formatDate(endDate, 'longOrdinalSlashed')}`;
};

const ShimmerLoader = () => {
  return (
    <Box aria-busy="true" aria-label="Loading..." role="status">
      <Table>
        <Table.Header>
          <Table.Row>
            {[...Array(4).keys()].map(key => (
              <Table.ColumnHeaderCell key={key}>
                <Shimmer
                  css={{
                    height: 'size.100',
                  }}
                />
              </Table.ColumnHeaderCell>
            ))}
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {[...Array(4).keys()].map(row => (
            <Table.Row key={row}>
              {[...Array(4).keys()].map(column => (
                <Table.Cell key={`${row}-${column}`}>
                  <Shimmer
                    css={{
                      alignItems: 'center',
                      height: 'size.200',
                      width: '70%',
                    }}
                  />
                </Table.Cell>
              ))}
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </Box>
  );
};

const DeconversionReportContentType = ({
  id,
  createdAt,
  liquidationDate,
  name,
  status,
  contentType,
  filename,
}: DeconversionReport) => {
  return (
    <Table.Row key={id}>
      <Table.Cell>{createdAt && formatDate(createdAt, 'longOrdinalSlashed')}</Table.Cell>
      <Table.Cell>{createdAt && formatDate(createdAt, 'longOrdinalSlashed')}</Table.Cell>
      <Table.Cell>
        <Text as="p">{name}</Text>
        <Text as="p" variant="text50" color="text.weak">
          Liquidation date: {liquidationDate && formatDate(liquidationDate, 'longOrdinalSlashed')}
        </Text>
      </Table.Cell>
      <Table.Cell>
        {isReportComplete(status) ? (
          <SambaAutoLink
            aria-label={`Download ${filename}`}
            to={urlBuilder.getDeconversionPackageDownload()}
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Icon name="Folder" size="xsmall" />
            {contentType}
          </SambaAutoLink>
        ) : (
          <Text as="p">Pending</Text>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const GeneralReportContentType = ({
  id,
  createdAt,
  startDate,
  endDate,
  name,
  filename,
  status,
  contentType,
}: GeneralReport) => {
  return (
    <Table.Row key={id}>
      <Table.Cell>{formatDate(createdAt, 'longOrdinalSlashed')}</Table.Cell>
      <Table.Cell>{formatDateRange(startDate, endDate)}</Table.Cell>
      <Table.Cell>{name}</Table.Cell>
      <Table.Cell>
        {isReportComplete(status) ? (
          <SambaAutoLink
            aria-label={`Download ${filename}`}
            to={urlBuilder.getReportCsvDownload(id)}
            download={filename}
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Icon name="DocumentPopulated" size="xsmall" />
            {contentType}
          </SambaAutoLink>
        ) : (
          <Text as="p">Pending</Text>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const InvestmentActivityPackageContentType = ({ id, createdAt, name, filename, status, contentType }: Report) => {
  return (
    <Table.Row key={id}>
      <Table.Cell>{formatDate(createdAt, 'longOrdinalSlashed')}</Table.Cell>
      <Table.Cell>{formatDate(createdAt, 'longOrdinalSlashed')}</Table.Cell>
      <Table.Cell>{name}</Table.Cell>
      <Table.Cell>
        {isReportComplete(status) ? (
          <SambaAutoLink
            aria-label={`Download ${filename}`}
            to={urlBuilder.getInvestmentActivityPackageDownload(id)}
            css={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Icon name="Folder" size="xsmall" />
            {contentType}
          </SambaAutoLink>
        ) : (
          <Text as="p">Pending</Text>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const ReportsTable = () => {
  const employerId = useCurrentEmployerId();
  const { data: rawData } = useGraphQLQuery(GeneratedReportsDocument, {
    variables: { employerId },
    throwOnError: true,
  });
  const data = rawData as GeneratedReportsQuery | undefined;

  if (!data) {
    return <ShimmerLoader />;
  }

  const isReportsEmpty = data?.employer.reports.length === 0;

  if (isReportsEmpty) {
    return (
      <Text color="text.weak" css={{ py: 'space.200', display: 'flex', justifyContent: 'center' }}>
        No reports generated yet
      </Text>
    );
  }

  return (
    <Table>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Date generated</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Report date</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Report name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Download</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {data?.employer.reports.map(report => {
          if (report.__typename === 'BenefitReport') {
            return <GeneralReportContentType key={report.id} {...report} />;
          }
          if (report.__typename === 'InvestmentActivityPackage') {
            return <InvestmentActivityPackageContentType key={report.id} {...report} />;
          }
          return <DeconversionReportContentType key={report.id} {...report} />;
        })}
      </Table.Body>
    </Table>
  );
};

const ReportBlurb = () => {
  return (
    <Text as="p" color="text.weak">
      The information in these reports is current as of the specified date, is based on information provided by the Plan
      Sponsor or its representative, and does not cover any period predating the retention of Betterment at Work. These
      reports should not be substituted for a certified financial statement.
    </Text>
  );
};

export const GeneratedReports = () => {
  return (
    <ErrorBoundary name="generatedReportsTable" fallbackType="full">
      <Box css={{ py: 'space.300' }}>
        <Card css={{ p: 'space.300', gap: 'space.300' }}>
          <Card.Header>
            <H2 variant="title200">Generated reports</H2>
          </Card.Header>
          <Separator />
          <Card.Content>
            <ReportsTable />
          </Card.Content>
        </Card>
      </Box>
      <ReportBlurb />
    </ErrorBoundary>
  );
};
